import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { AttractionCard, thumbUrl } from '../AttractionCard';
import { byRankScore } from '../../utils/rank';

const THEME_META = {
  culture: {
    icon: '🏛️',
    gradient: 'linear-gradient(160deg, #1e3a5f 0%, #0f2346 100%)',
    accent: '#93c5fd',
    tagBg: '#dbeafe',
    tagText: '#1e40af',
    zh:   { title:'文化體驗', subtitle:'博物館 · 歷史古蹟 · 在地文化', intro:'透過博物館、古蹟和傳統藝術，深入了解目的地的歷史與文化底蘊，感受獨一無二的在地生活方式。', highlights:['探訪世界級博物館與藝術館','漫遊歷史街區與古老寺廟','體驗正宗在地傳統與節慶'] },
    zhCN: { title:'文化体验', subtitle:'博物馆 · 历史古迹 · 在地文化', intro:'透过博物馆、古迹和传统艺术，深入了解目的地的历史与文化底蕴，感受独一无二的在地生活方式。', highlights:['探访世界级博物馆与艺术馆','漫游历史街区与古老寺庙','体验正宗在地传统与节庆'] },
    en:   { title:'Culture', subtitle:'Museums · Heritage · Local Culture', intro:'Immerse yourself in the history and traditions of your destination through world-class museums, ancient heritage sites, and authentic local experiences.', highlights:['Explore world-class museums & galleries','Wander through historic districts & temples','Discover local traditions & festivals'] },
    ja:   { title:'文化体験', subtitle:'博物館・遺跡・地元文化', intro:'世界クラスの博物館や歴史的な遺跡、伝統芸能を通じて、目的地の歴史と文化を深く体感してください。', highlights:['世界クラスの博物館・美術館を巡る','歴史地区や古寺を散策する','地元の伝統と祭りを体験する'] },
    ko:   { title:'문화 체험', subtitle:'박물관 · 유적 · 현지 문화', intro:'세계 수준의 박물관, 역사 유적지, 전통 문화 체험을 통해 목적지의 역사와 문화를 깊이 느껴보세요.', highlights:['세계 수준의 박물관 & 갤러리 탐방','역사 지구와 사찰 산책','현지 전통과 축제 체험'] },
  },
  nature: {
    icon: '🌿',
    gradient: 'linear-gradient(160deg, #14532d 0%, #052e16 100%)',
    accent: '#4ade80',
    tagBg: '#dcfce7',
    tagText: '#166534',
    zh:   { title:'大自然', subtitle:'公園 · 海灘 · 山野探索', intro:'走進大自然，感受青山綠水帶來的寧靜與活力。從城市公園到偏遠海灘，每一片自然風光都值得探索。', highlights:['穿越翠綠的山徑與森林步道','漫步迷人的海灘與海岸線','尋訪隱秘的瀑布與自然保護區'] },
    zhCN: { title:'大自然', subtitle:'公园 · 海滩 · 山野探索', intro:'走进大自然，感受青山绿水带来的宁静与活力。从城市公园到偏远海滩，每一片自然风光都值得探索。', highlights:['穿越翠绿的山径与森林步道','漫步迷人的海滩与海岸线','寻访隐秘的瀑布与自然保护区'] },
    en:   { title:'Nature', subtitle:'Parks · Beaches · Wilderness', intro:'Escape the city and reconnect with nature. From lush hiking trails to pristine beaches and hidden waterfalls, let the great outdoors rejuvenate your spirit.', highlights:['Hike through lush mountain trails & forests','Relax on stunning beaches & coastlines','Discover hidden waterfalls & nature reserves'] },
    ja:   { title:'大自然', subtitle:'公園・ビーチ・アウトドア', intro:'都市を離れ、豊かな自然の中に身を置きましょう。ハイキングコースから美しいビーチ、隠れた滝まで、自然が心を癒してくれます。', highlights:['緑豊かな山道・森林トレイルをハイク','美しいビーチ・海岸線でリラックス','隠れた滝・自然保護区を発見'] },
    ko:   { title:'대자연', subtitle:'공원 · 해변 · 야외 탐험', intro:'도시를 벗어나 자연과 함께하세요. 울창한 등산로부터 아름다운 해변, 숨겨진 폭포까지, 대자연이 마음을 치유해 드립니다.', highlights:['울창한 산길 & 삼림 트레일 하이킹','아름다운 해변 & 해안선에서 휴식','숨겨진 폭포 & 자연보호구역 발견'] },
  },
  play: {
    icon: '🎡',
    gradient: 'linear-gradient(160deg, #4c1d95 0%, #2d1582 100%)',
    accent: '#c4b5fd',
    tagBg: '#ede9fe',
    tagText: '#5b21b6',
    zh:   { title:'玩樂', subtitle:'主題樂園 · 娛樂 · 夜生活', intro:'盡情享受目的地最刺激的娛樂體驗，從主題樂園的過山車到精彩的夜生活，讓旅途充滿歡笑與活力。', highlights:['暢玩世界知名主題樂園','體驗精彩表演與文化演出','探索充滿魅力的夜生活'] },
    zhCN: { title:'玩乐', subtitle:'主题乐园 · 娱乐 · 夜生活', intro:'尽情享受目的地最刺激的娱乐体验，从主题乐园的过山车到精彩的夜生活，让旅途充满欢笑与活力。', highlights:['畅玩世界知名主题乐园','体验精彩表演与文化演出','探索充满魅力的夜生活'] },
    en:   { title:'Entertainment', subtitle:'Theme Parks · Shows · Nightlife', intro:'Make unforgettable memories with thrilling entertainment options. From iconic theme parks to dazzling shows and vibrant nightlife, fun awaits at every turn.', highlights:['Thrill rides at world-famous theme parks','Catch spectacular shows & performances','Experience the best of local nightlife'] },
    ja:   { title:'エンタメ', subtitle:'テーマパーク・ショー・ナイトライフ', intro:'テーマパークのスリルから華やかなショー、活気あふれるナイトライフまで、忘れられない体験があなたを待っています。', highlights:['世界的テーマパークのアトラクションを楽しむ','華やかなショー・パフォーマンスを観賞','ローカルナイトライフの魅力を体験'] },
    ko:   { title:'엔터테인먼트', subtitle:'테마파크 · 공연 · 나이트라이프', intro:'세계적인 테마파크의 스릴부터 화려한 공연, 활기찬 나이트라이프까지, 잊지 못할 즐거움이 기다립니다.', highlights:['세계적인 테마파크 어트랙션 즐기기','화려한 공연 & 퍼포먼스 관람','현지 나이트라이프의 매력 체험'] },
  },
  shopping: {
    icon: '🛍️',
    gradient: 'linear-gradient(160deg, #9f1239 0%, #6b0f2c 100%)',
    accent: '#fda4af',
    tagBg: '#ffe4e6',
    tagText: '#9f1239',
    zh:   { title:'購物', subtitle:'商場 · 市場 · 特色小店', intro:'從奢華品牌旗艦店到充滿活力的街頭市場，目的地的購物體驗豐富多樣。帶走最具紀念價值的戰利品！', highlights:['掃貨高端購物商場與品牌旗艦店','尋訪充滿特色的本地市集','發現獨家限定商品與手工藝品'] },
    zhCN: { title:'购物', subtitle:'商场 · 市场 · 特色小店', intro:'从奢华品牌旗舰店到充满活力的街头市场，目的地的购物体验丰富多样。带走最具纪念价值的战利品！', highlights:['扫货高端购物商场与品牌旗舰店','寻访充满特色的本地市集','发现独家限定商品与手工艺品'] },
    en:   { title:'Shopping', subtitle:'Malls · Markets · Boutiques', intro:'From luxury flagship stores to lively street markets, shopping here is an experience in itself. Discover exclusive finds, unique souvenirs and local crafts.', highlights:['Browse luxury malls & brand flagships','Hunt for treasures at local markets','Find exclusive items & handmade crafts'] },
    ja:   { title:'ショッピング', subtitle:'ショッピングモール・マーケット・ブティック', intro:'高級ブランドの旗艦店から活気あふれる市場まで、ショッピングそのものが体験です。限定品や地元の工芸品を探しましょう。', highlights:['高級ショッピングモール・ブランドを巡る','ローカルマーケットで宝探し','限定品・手作り工芸品を発見'] },
    ko:   { title:'쇼핑', subtitle:'쇼핑몰 · 시장 · 부티크', intro:'명품 플래그십 스토어부터 활기찬 거리 시장까지, 쇼핑 자체가 하나의 경험입니다. 한정판 아이템과 현지 공예품을 찾아보세요.', highlights:['럭셔리 쇼핑몰 & 브랜드 플래그십 탐방','현지 시장에서 보물찾기','한정판 & 수제 공예품 발견'] },
  },
  food: {
    icon: '🍜',
    gradient: 'linear-gradient(160deg, #c2410c 0%, #7c2d12 100%)',
    accent: '#fed7aa',
    tagBg: '#ffedd5',
    tagText: '#c2410c',
    zh:   { title:'美食', subtitle:'餐廳 · 街頭小食 · 美食市集', intro:'每一口都是一次文化旅程。從米其林餐廳到深夜街頭小食，感受目的地最真實的飲食文化與在地風味。', highlights:['品嚐正宗在地傳統料理','探索隱藏版美食小店','遊覽熱鬧的夜市與美食市集'] },
    zhCN: { title:'美食', subtitle:'餐厅 · 街头小食 · 美食市集', intro:'每一口都是一次文化旅程。从米其林餐厅到深夜街头小食，感受目的地最真实的饮食文化与在地风味。', highlights:['品尝正宗在地传统料理','探索隐藏版美食小店','游览热闹的夜市与美食市集'] },
    en:   { title:'Food & Dining', subtitle:'Restaurants · Street Food · Food Markets', intro:'Every bite is a journey through local culture. From Michelin-starred restaurants to late-night street stalls, savour the authentic flavours of your destination.', highlights:['Savour authentic local cuisine & specialities','Discover hidden gem eateries & hawker stalls','Explore vibrant night markets & food fairs'] },
    ja:   { title:'グルメ', subtitle:'レストラン・屋台・フードマーケット', intro:'一口ごとに文化の旅が始まります。ミシュランレストランから深夜の屋台まで、目的地の本場の味を楽しんでください。', highlights:['正宗な郷土料理・名物を味わう','隠れた名店・ホーカーズを発見','活気あふれる夜市・フードフェアを探索'] },
    ko:   { title:'미식', subtitle:'레스토랑 · 길거리 음식 · 푸드 마켓', intro:'한 입 한 입이 현지 문화로의 여행입니다. 미슐랭 레스토랑부터 심야 길거리 음식까지, 목적지의 진짜 맛을 즐겨보세요.', highlights:['정통 현지 요리 & 명물 맛보기','숨은 맛집 & 호커 스탠드 발견','활기찬 야시장 & 푸드 페어 탐방'] },
  },
};


const PAGE_SIZE = 8;

const EXPLORE_LABEL = { 'zh-HK':'主題探索', 'zh-CN':'主题探索', en:'Explore by Theme', ja:'テーマ別探索', ko:'테마별 탐색' };
const COUNT_LABEL   = { 'zh-HK':'個景點', 'zh-CN':'个景点', en:' spots', ja:'件', ko:'개 명소' };
const EMPTY_LABEL   = { 'zh-HK':'此城市暫無該主題景點', 'zh-CN':'此城市暂无该主题景点', en:'No spots found for this theme in the current city', ja:'現在の都市にこのテーマのスポットがありません', ko:'현재 도시에 이 테마의 명소가 없습니다' };
const BACK_LABEL    = { 'zh-HK':'返回', 'zh-CN':'返回', en:'Back', ja:'戻る', ko:'돌아가기' };
const HIGHLIGHTS_LABEL = { 'zh-HK':'精選體驗', 'zh-CN':'精选体验', en:'Top Highlights', ja:'おすすめ体験', ko:'추천 체험' };
const SPOTS_LABEL   = { 'zh-HK':'精選景點', 'zh-CN':'精选景点', en:'Featured Spots', ja:'おすすめスポット', ko:'추천 명소' };

export const THEME_ORDER = ['culture','nature','play','shopping','food'];

// Picks a representative photo for each theme card: the highest-rated
// attraction in that category that actually has an image, so the strip
// shows real destination photography instead of a flat emoji tile.
function pickRepImage(attractions, themeKey) {
  const candidates = attractions.filter(a => a.category === themeKey && a.imageUrl);
  if (candidates.length === 0) return null;
  candidates.sort(byRankScore);
  return candidates[0].imageUrl;
}

export function ThemeEntryStrip({ lang, onThemeSelect, attractions = [], activeTheme = null }) {
  const counts = {};
  THEME_ORDER.forEach(k => { counts[k] = attractions.filter(a => a.category === k).length; });
  const repImages = {};
  THEME_ORDER.forEach(k => { repImages[k] = pickRepImage(attractions, k); });

  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontSize:17, fontWeight:900, color:'#0f172a', margin:'0 0 12px' }}>{EXPLORE_LABEL[lang] || EXPLORE_LABEL['zh-HK']}</p>
      <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:6, scrollbarWidth:'none', msOverflowStyle:'none', marginLeft:-20, marginRight:-20, paddingLeft:20, paddingRight:20 }}>
        {THEME_ORDER.map(themeKey => {
          const m = THEME_META[themeKey];
          const copy = m[lang === 'zh-CN' ? 'zhCN' : lang === 'en' ? 'en' : lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : 'zh'];
          const cnt = counts[themeKey];
          const isActive = activeTheme === themeKey;
          const repImage = repImages[themeKey];
          return (
            <button key={themeKey} onClick={() => onThemeSelect(themeKey)}
              style={{ flexShrink:0, width:130, height:160, borderRadius:20, overflow:'hidden', border: isActive ? `2.5px solid ${m.accent}` : '2.5px solid transparent', cursor:'pointer', position:'relative',
                background: repImage
                  ? `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.75) 100%), url(${thumbUrl(repImage, 260)}) center/cover no-repeat`
                  : m.gradient,
                padding:0, display:'flex', flexDirection:'column', justifyContent:'flex-end', transition:'transform 0.2s, box-shadow 0.2s, border-color 0.2s', boxShadow: isActive ? `0 0 0 3px ${m.accent}40, 0 12px 28px rgba(0,0,0,0.25)` : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; if(!isActive) e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; if(!isActive) e.currentTarget.style.boxShadow = 'none'; }}>
              {/* Small icon badge — kept for quick recognition even with a photo background */}
              <span style={{ position:'absolute', top:10, left:10, fontSize: repImage ? 18 : 40, ...(repImage ? { background:'rgba(0,0,0,0.35)', borderRadius:10, padding:'4px 7px', backdropFilter:'blur(4px)' } : { left:'50%', top:16, transform:'translateX(-50%)' }) }}>{m.icon}</span>
              {/* Count badge */}
              {cnt > 0 && (
                <span style={{ position:'absolute', top:10, right:10, background:'rgba(0,0,0,0.35)', color:'white', fontSize:10, fontWeight:900, padding:'2px 8px', borderRadius:20, backdropFilter:'blur(4px)' }}>
                  {cnt}
                </span>
              )}
              {/* Bottom text */}
              <div style={{ padding:'0 12px 14px', textAlign:'center', width:'100%', boxSizing:'border-box' }}>
                <p style={{ margin:'0 0 3px', fontSize:14, fontWeight:900, color:'white', lineHeight:1.2 }}>{copy.title}</p>
                <p style={{ margin:0, fontSize:9, color:`${m.accent}cc`, fontWeight:700, lineHeight:1.4 }}>{copy.subtitle}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ALL_LABEL     = { 'zh-HK':'全部', 'zh-CN':'全部', en:'All', ja:'すべて', ko:'전체' };
const SUB_FILTER_LABEL = { 'zh-HK':'類別篩選', 'zh-CN':'类别筛选', en:'Filter by type', ja:'タイプ別', ko:'유형별 필터' };

export default function ThemePage({ theme, lang, t, city, attractions, favorites, onToggleFav, onSelect, onBack }) {
  const [page, setPage] = useState(0);
  const [subFilter, setSubFilter] = useState('all');
  useEffect(() => { setSubFilter('all'); setPage(0); }, [theme]);
  const m    = THEME_META[theme] || THEME_META.culture;
  const copy = m[lang === 'zh-CN' ? 'zhCN' : lang === 'en' ? 'en' : lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : 'zh'];

  const themeItems = (attractions || []).filter(a => a.category === theme);
  // Dynamic specify_cat chips from actual data
  const specifyOptions = [...new Set(themeItems.filter(a => a.specify_cat).map(a => a.specify_cat))].sort();
  const filtered = subFilter === 'all'
    ? themeItems
    : themeItems.filter(a => a.specify_cat === subFilter);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const countLabel  = COUNT_LABEL[lang]   || COUNT_LABEL['zh-HK'];
  const emptyLabel  = EMPTY_LABEL[lang]   || EMPTY_LABEL['zh-HK'];
  const backLabel   = BACK_LABEL[lang]    || BACK_LABEL['zh-HK'];
  const hiLabel     = HIGHLIGHTS_LABEL[lang] || HIGHLIGHTS_LABEL['zh-HK'];
  const spotsLabel  = SPOTS_LABEL[lang]   || SPOTS_LABEL['zh-HK'];

  return (
    <div style={{ minHeight:'100vh', background:'#f8fafc', paddingBottom:120 }}>

      {/* Hero header */}
      <div style={{ background: m.gradient, padding:'32px 20px 28px', position:'relative', overflow:'hidden' }}>
        {/* Decorative circle */}
        <div style={{ position:'absolute', top:-40, right:-40, width:180, height:180, borderRadius:'50%', background:`${m.accent}15`, pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:`${m.accent}10`, pointerEvents:'none' }}/>

        <button onClick={onBack}
          style={{ background:'rgba(255,255,255,0.12)', border:'none', color:'rgba(255,255,255,0.85)', borderRadius:10, padding:'6px 14px', fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:24, display:'flex', alignItems:'center', gap:6 }}>
          <ChevronLeft size={14}/> {backLabel}
        </button>

        <div style={{ display:'flex', alignItems:'flex-start', gap:18 }}>
          <span style={{ fontSize:52, lineHeight:1, flexShrink:0 }}>{m.icon}</span>
          <div>
            <p style={{ margin:'0 0 4px', fontSize:26, fontWeight:900, color:'white', lineHeight:1.1 }}>{copy.title}</p>
            <p style={{ margin:'0 0 10px', fontSize:12, color:`${m.accent}`, fontWeight:700 }}>{copy.subtitle}</p>
            {filtered.length > 0 && (
              <span style={{ background:`${m.accent}25`, color:m.accent, fontSize:11, fontWeight:900, padding:'3px 10px', borderRadius:20 }}>
                {filtered.length}{countLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:720, margin:'0 auto', padding:'20px 20px' }}>

        {/* Intro */}
        <div style={{ background:'white', borderRadius:16, padding:'16px 18px', marginBottom:20, border:'1.5px solid #e2e8f0', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <p style={{ margin:0, fontSize:13.5, color:'#334155', lineHeight:1.8 }}>{copy.intro}</p>
        </div>

        {/* Highlights */}
        <div style={{ marginBottom:24 }}>
          <p style={{ fontSize:14, fontWeight:900, color:'#0f172a', margin:'0 0 12px' }}>{hiLabel}</p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {copy.highlights.map((h, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, background:'white', borderRadius:12, padding:'12px 16px', border:'1.5px solid #e2e8f0' }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{['✦','◈','⬡'][i]}</span>
                <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#0f172a' }}>{h}</p>
              </div>
            ))}
          </div>
        </div>

        {/* specify_cat chip filter — dynamic from actual data */}
        {specifyOptions.length > 0 && (
          <div style={{ marginBottom:20 }}>
            <p style={{ fontSize:12, fontWeight:700, color:'#64748b', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:0.8 }}>
              {SUB_FILTER_LABEL[lang] || SUB_FILTER_LABEL['zh-HK']}
            </p>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
              <button onClick={() => { setSubFilter('all'); setPage(0); }}
                style={{ padding:'6px 16px', borderRadius:20, border:'1.5px solid', fontSize:12, fontWeight:900, cursor:'pointer', transition:'all 0.15s',
                  borderColor: subFilter==='all' ? m.accent : '#e2e8f0',
                  background:  subFilter==='all' ? `${m.accent}20` : 'white',
                  color:       subFilter==='all' ? m.tagText : '#64748b' }}>
                {ALL_LABEL[lang] || ALL_LABEL['zh-HK']}
                <span style={{ marginLeft:5, fontSize:10, opacity:0.75 }}>({themeItems.length})</span>
              </button>
              {specifyOptions.map(sc => {
                const active = subFilter === sc;
                const cnt = themeItems.filter(a => a.specify_cat === sc).length;
                return (
                  <button key={sc} onClick={() => { setSubFilter(sc); setPage(0); }}
                    style={{ padding:'6px 16px', borderRadius:20, border:'1.5px solid', fontSize:12, fontWeight:900, cursor:'pointer', transition:'all 0.15s',
                      borderColor: active ? m.accent : '#e2e8f0',
                      background:  active ? `${m.accent}20` : 'white',
                      color:       active ? m.tagText : '#64748b' }}>
                    {sc}
                    <span style={{ marginLeft:5, fontSize:10, opacity:0.75 }}>({cnt})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Attractions */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <p style={{ fontSize:14, fontWeight:900, color:'#0f172a', margin:0 }}>{spotsLabel}</p>
            {totalPages > 1 && (
              <span style={{ fontSize:11, color:'#94a3b8', fontWeight:700 }}>{page+1} / {totalPages}</span>
            )}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8', fontSize:13, background:'white', borderRadius:16, border:'1.5px solid #e2e8f0' }}>
              <span style={{ fontSize:36, display:'block', marginBottom:12 }}>{m.icon}</span>
              {emptyLabel}
            </div>
          ) : (
            <>
              <div style={{ position:'relative' }}>
                {totalPages > 1 && page > 0 && (
                  <button aria-label="上一頁" onClick={() => setPage(p => Math.max(0, p-1))}
                    style={{ position:'absolute', left:-20, top:'50%', transform:'translateY(-50%)', zIndex:10, width:44, height:44, borderRadius:'50%', border:'1.5px solid #e2e8f0', background:'white', color:'#374151', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(0,0,0,0.1)' }}>‹</button>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(148px,1fr))', gap:12 }}>
                  {pageItems.map((a, i) => (
                    <AttractionCard key={page*PAGE_SIZE+i} a={a} idx={page*PAGE_SIZE+i} cityKey={city} t={t} lang={lang} favorites={favorites} onToggleFav={onToggleFav} onSelect={onSelect}/>
                  ))}
                </div>
                {totalPages > 1 && page < totalPages-1 && (
                  <button aria-label="下一頁" onClick={() => setPage(p => Math.min(totalPages-1, p+1))}
                    style={{ position:'absolute', right:-20, top:'50%', transform:'translateY(-50%)', zIndex:10, width:44, height:44, borderRadius:'50%', border:'1.5px solid #e2e8f0', background:'white', color:'#374151', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(0,0,0,0.1)' }}>›</button>
                )}
              </div>

              {totalPages > 1 && (
                <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:14 }}>
                  {Array.from({ length:totalPages }, (_, i) => (
                    <button key={i} aria-label={`第 ${i+1} 頁`} onClick={() => setPage(i)}
                      style={{ width:i===page?20:7, height:7, borderRadius:4, border:'none', background:i===page?'#f59e0b':'#e2e8f0', cursor:'pointer', padding:0, transition:'all 0.25s' }}/>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
